// src/admin/pages/AdminSeoManager.js
import React, { useState, useEffect, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaSave, FaImage } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { getSeoByPage, saveSeoByPage } from "../../services/adminSeo.api";
import { getPublicCategories } from "../../services/categories.api";
import { FaSearch } from "react-icons/fa"; // add at top

function AdminSeoManager() {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const { lang } = useContext(AdminLanguageContext);

  const ACTIVE_SEO_PAGES = ["home", "products", "product", "category"];

  const TEXT = {
    EN: {
      title: "SEO Manager",
      choosePage: "Choose a Page",
      chooseCategory: "Choose a Category",
      loading: "Loading SEO data...",
      metaTitle: "Meta Title",
      metaDesc: "Meta Description",
      keywords: "Keywords (comma separated)",
      canonical: "Canonical URL",
      indexable: "Allow Indexing (index/follow)",
      ogTitle: "OG Title",
      ogDesc: "OG Description",
      ogImage: "OG Image",
      jsonld: "JSON-LD Structured Data",
      save: "Save SEO Settings",
      saved: "SEO settings saved successfully",
      pages: {
        home: "Home Page",
        about: "About Us",
        contact: "Contact Us",
        faq: "FAQ",
        products: "Products Listing",
        product: "Single Product Page",
        category: "Category Page",
      },
    },
    AR: {
      title: "إدارة السيو",
      choosePage: "اختر الصفحة",
       saved: "تم حفظ إعدادات السيو بنجاح",
      chooseCategory: "اختر التصنيف",
      loading: "جاري تحميل بيانات السيو...",
      metaTitle: "عنوان الميتا",
      metaDesc: "وصف الميتا",
      keywords: "الكلمات المفتاحية (مفصولة بفاصلة)",
      canonical: "الرابط الأساسي (Canonical)",
      indexable: "السماح بالأرشفة (index/follow)",
      ogTitle: "عنوان OG",
      ogDesc: "وصف OG",
      ogImage: "صورة OG",
      jsonld: "بيانات JSON-LD المنظمة",
      save: "حفظ إعدادات السيو",
       saved: "تم حفظ إعدادات السيو بنجاح",
      pages: {
        home: "الصفحة الرئيسية",
        about: "من نحن",
        contact: "اتصل بنا",
        faq: "الأسئلة الشائعة",
        products: "قائمة المنتجات",
        product: "صفحة المنتج",
        category: "صفحة التصنيف",
      },
    },
  }[lang];

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
const isAdmin = adminUser.role === "admin";

const canAccess = isAdmin || perms.can_manage_seo;


  const [page, setPage] = useState("home");

  // ✅ categories for category SEO
  const [categories, setCategories] = useState([]);
  const [categorySlug, setCategorySlug] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState("");
  const [canonical, setCanonical] = useState("");
  const [indexable, setIndexable] = useState(true);

  const [ogTitle, setOgTitle] = useState("");
  const [ogDescription, setOgDescription] = useState("");
  const [ogImageUrl, setOgImageUrl] = useState("");
  const [jsonLd, setJsonLd] = useState("");

// ✅ NEW
const [toast, setToast] = useState("");

  // ✅ load categories once (for category SEO)
  useEffect(() => {
    getPublicCategories()
      .then((rows) => {
        const list = rows || [];
        setCategories(list);
        // if first time, set default slug
        if (!categorySlug && list.length) {
          setCategorySlug(list[0].slug);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine entityId for current page
  const entityId = page === "category" ? categorySlug || null : null;

  // ✅ load SEO when page changes OR category slug changes (if category)
  useEffect(() => {
    setLoading(true);

    getSeoByPage(page, entityId)
      .then((data) => {
        // reset fields if no data
        if (!data) {
          setTitle("");
          setDescription("");
          setKeywords("");
          setCanonical("");
          setIndexable(true);
          setOgTitle("");
          setOgDescription("");
          setOgImageUrl("");
          setJsonLd("");
          return;
        }

        setTitle(data.title || "");
        setDescription(data.meta_description || "");
        setKeywords(data.meta_keywords || "");
        setCanonical(data.canonical || "");
        setIndexable(!!data.is_indexable);
        setOgTitle(data.title || "");
        setOgDescription(data.meta_description || "");
        setOgImageUrl(data.og_image_url || "");
        setJsonLd(data.json_ld || "");
      })
      .finally(() => setLoading(false));
  }, [page, entityId]);

  const showToast = (msg) => {
  setToast(msg);
  setTimeout(() => setToast(""), 2500);
};

  const handleSave = async () => {
  const cleanJsonLd = String(jsonLd || "").trim();

  if (cleanJsonLd) {
    try {
      JSON.parse(cleanJsonLd);
    } catch {
      showToast(
        lang === "AR"
          ? "JSON-LD غير صالح. يرجى تصحيحه قبل الحفظ."
          : "Invalid JSON-LD. Please fix it before saving."
      );
      return;
    }
  }

  await saveSeoByPage(page, entityId, {
    title,
    meta_description: description,
    meta_keywords: keywords,
    canonical,
    og_image_url: ogImageUrl || null,
    is_indexable: indexable,
    json_ld: cleanJsonLd || null,
  });

  showToast(TEXT.saved);
};

  const categoryLabel = (c) => (lang === "EN" ? c.name_en : c.name_ar);

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className="flex-1 lg:ml-64 transition-all duration-300">
        <AdminHeader setIsOpen={setIsOpen} />

<main className="p-4 sm:p-6 animate-fadeSlide max-w-5xl mx-auto w-full">
        {/* 🚫 Mobile block message */}
<div>
  {!canAccess ? (
    <div className="bg-white border border-red-200 rounded-xl p-5 text-center">
      <p className="text-red-700 font-semibold">
        {lang === "AR"
          ? "غير مصرح لك بإدارة إعدادات SEO"
          : "You are not allowed to manage SEO settings"}
      </p>
    </div>
  ) : (
    <>
     <div className="flex items-center justify-between mb-5">
  <div className="flex items-center gap-2">
    <div className="w-6 h-6 flex items-center justify-center">
      <FaSearch className="text-gray-900 text-[18px]" />
    </div>

    <h1 className="text-[20px] font-semibold text-gray-900">
      {TEXT.title}
    </h1>
  </div>
</div>

          <div className="mb-6">
            <label className="text-sm text-gray-900 mb-4">{TEXT.choosePage}</label>
            <select
             className="
w-full
h-12 sm:h-11
px-3
border border-gray-300
rounded-lg
bg-white
text-sm
shadow-sm
outline-none
focus:ring-2 focus:ring-gray-200
"
              value={page}
              onChange={(e) => setPage(e.target.value)}
            >
              {Object.entries(TEXT.pages)
                .filter(([key]) => ACTIVE_SEO_PAGES.includes(key))
                .map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
            </select>
          </div>

          {/* ✅ Category selector appears ONLY for category page */}
          {page === "category" && (
            <div className="mb-6">
              <label className="text-sm text-gray-900">{TEXT.chooseCategory}</label>
              <select
               className="
w-full
h-12 sm:h-11
px-3
border border-gray-300
rounded-lg
bg-white
text-sm
shadow-sm
outline-none
focus:ring-2 focus:ring-gray-200
"
                value={categorySlug}
                onChange={(e) => setCategorySlug(e.target.value)}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.slug}>
                    {categoryLabel(c)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loading ? (
            <p className="text-gray-600">{TEXT.loading}</p>
          ) : (
           <div className="space-y-5 sm:space-y-6">

  {/* ✅ BASIC SEO */}
  <div className="bg-white border border-[#e3e3e3] rounded-xl p-5 shadow-sm">
    <h2 className="text-sm font-semibold text-gray-900 mb-4">
      {lang === "AR" ? "إعدادات SEO الأساسية" : "Basic SEO"}
    </h2>

    <div className="space-y-4">
      {/* Meta Title */}
      <div>
        <label className="text-sm text-gray-900">{TEXT.metaTitle}</label>
        <input
  type="text"
  value={title}
  onChange={(e) => setTitle(e.target.value)}
  className="
w-full
h-12 sm:h-11
px-3
border border-gray-300
rounded-lg
bg-white
text-sm
shadow-sm
outline-none
focus:ring-2 focus:ring-gray-200
"
/>
      </div>

      {/* Meta Description */}
      <div>
        <label className="text-sm text-gray-900">{TEXT.metaDesc}</label>
       <textarea
  rows={3}
  value={description}
  onChange={(e) => setDescription(e.target.value)}
  className="
w-full
h-12 sm:h-11
px-3
border border-gray-300
rounded-lg
bg-white
text-sm
shadow-sm
outline-none
focus:ring-2 focus:ring-gray-200
"
/>
      </div>

      {/* Keywords */}
      <div>
        <label className="text-sm text-gray-900">{TEXT.keywords}</label>
        <input type="text" value={keywords} onChange={(e) => setKeywords(e.target.value)} className="
w-full
h-12 sm:h-11
px-3
border border-gray-300
rounded-lg
bg-white
text-sm
shadow-sm
outline-none
focus:ring-2 focus:ring-gray-200
" />
      </div>
    </div>
  </div>

  {/* ✅ OPEN GRAPH */}
  <div className="bg-white border border-[#e3e3e3] rounded-xl p-5 shadow-sm">
    <h2 className="text-sm font-semibold text-gray-900 mb-4">
      Open Graph (OG)
    </h2>

    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-900">{TEXT.ogTitle}</label>
        <input
  type="text"
  value={title}
  readOnly
  className="
    w-full
    h-12 sm:h-11
    px-3
    border border-gray-300
    rounded-lg
    bg-gray-50
    text-sm
    shadow-sm
    outline-none
    text-gray-500
  "
/>
<p className="mt-1 text-xs text-gray-500">
  {lang === "AR"
    ? "يتم استخدام عنوان الميتا كعنوان OG تلقائياً"
    : "Meta title is used automatically as OG title"}
</p>
      </div>

      <div>
        <label className="text-sm text-gray-900">{TEXT.ogDesc}</label>
    <textarea
  rows={3}
  value={description}
  readOnly
  className="
    w-full
    min-h-[90px]
    px-3 py-2
    border border-gray-300
    rounded-lg
    bg-gray-50
    text-sm
    shadow-sm
    outline-none
    text-gray-500
  "
/>
<p className="mt-1 text-xs text-gray-500">
  {lang === "AR"
    ? "يتم استخدام وصف الميتا كوصف OG تلقائياً"
    : "Meta description is used automatically as OG description"}
</p>
      </div>

      <div>
        <label className="text-sm text-gray-900">{TEXT.ogImage}</label>
        <input type="text" value={ogImageUrl} onChange={(e) => setOgImageUrl(e.target.value)} className="
w-full
h-12 sm:h-11
px-3
border border-gray-300
rounded-lg
bg-white
text-sm
shadow-sm
outline-none
focus:ring-2 focus:ring-gray-200
" />
      </div>
    </div>
  </div>

  {/* ✅ ADVANCED */}
  <div className="bg-white border border-[#e3e3e3] rounded-xl p-5 shadow-sm">
    <h2 className="text-sm font-semibold text-gray-900 mb-4">
      {lang === "AR" ? "إعدادات متقدمة" : "Advanced"}
    </h2>

    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-900">{TEXT.canonical}</label>
        <input type="text" value={canonical} onChange={(e) => setCanonical(e.target.value)} className="
w-full
h-12 sm:h-11
px-3
border border-gray-300
rounded-lg
bg-white
text-sm
shadow-sm
outline-none
focus:ring-2 focus:ring-gray-200
" />
      </div>

      <div className="flex items-center gap-2">
        <input type="checkbox" checked={indexable} onChange={(e) => setIndexable(e.target.checked)} />
        <label className="text-sm text-gray-900">{TEXT.indexable}</label>
      </div>

      <div>
        <label className="text-sm text-gray-900">{TEXT.jsonld}</label>
  <textarea
  rows={8}
  value={jsonLd}
  onChange={(e) => setJsonLd(e.target.value)}
  placeholder='{"@context":"https://schema.org","@type":"WebPage"}'
  className="
    w-full
    min-h-[180px]
    px-3 py-2
    border border-gray-300
    rounded-lg
    bg-white
    text-sm
    font-mono
    shadow-sm
    outline-none
    focus:ring-2 focus:ring-gray-200
  "
/>
      </div>
    </div>
  </div>

  {/* ✅ SAVE BUTTON */}
  <button
    onClick={handleSave}
    className="
h-10 px-4
bg-[#1f1f1f]
text-white
rounded-lg
shadow-[0_1px_0_rgba(0,0,0,0.08),0_3px_8px_rgba(0,0,0,0.2)]
text-sm font-medium
hover:bg-black
active:shadow-[inset_0_2px_6px_rgba(0,0,0,0.3)]
transition
flex items-center gap-2
"
  >
    {TEXT.save}
  </button>

</div>
          )}
            </>
  )}
</div>
{toast && (
  <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
    <div
      className={`
        bg-black text-white
        px-5 py-3
        rounded-xl
        shadow-[0_10px_30px_rgba(0,0,0,0.25)]
        text-sm font-medium
        animate-fadeIn
        ${lang === "AR" ? "text-right" : ""}
      `}
    >
      {toast}
    </div>
  </div>
)}
</main>

      </div>
    </div>
  );
}

export default AdminSeoManager;
